"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import faqData from "@/data/FaqData";
import { useLanguage } from "@/utils/LanguageContext";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";

import faqThumb from "@/assets/img/update/normal/faq.png";

const FaqArea = () => {
  const { lang, t } = useLanguage();
  const [openId, setOpenId] = useState<number | null>(faqData[0]?.id ?? null);

  const toggleItem = (id: number) => {
    setOpenId((currentId) => (currentId === id ? null : id));
  };

  return (
    <section
      className="pt-140 pb-140 overflow-hidden faq-section-revamp home-anchor-target"
      id="faqArea"
      aria-labelledby="faq-title"
    >
      <div className="container">
        <div className="faq-shell">
          <div className="row g-4 align-items-stretch faq-grid">
            <div className="col-lg-5 col-xl-4">
              <div className="faq-sidebar">
                <div className="section-title mb-0">
                  <span className="faq-eyebrow">{t("faq_section_eyebrow")}</span>
                  <h2 className="title style2" id="faq-title">{t("Frequently Asked Questions")}</h2>
                  <p className="sec-text faq-intro-copy">{t("faq_section_intro")}</p>
                </div>

                <div className="faq-pill-list" aria-label={t("Frequently Asked Questions")}>
                  {["faq_pill_onchain", "faq_pill_governance", "faq_pill_research"].map((key) => (
                    <span key={key} className="faq-pill">
                      {t(key)}
                    </span>
                  ))}
                </div>

                <div className="faq-links">
                  <Link
                    href="/flipbook/whitepaper"
                    prefetch={false}
                    className="ts-buy-btn faq-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Read Whitepaper →")}
                  </Link>
                  <Link
                    href="https://simulation.invtron.com"
                    className="ts-buy-btn faq-link faq-link-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Tokenomics Simulation (ROI) →")}
                  </Link>
                  <Link href={`/${lang}/contact`} className="ts-buy-btn faq-link faq-link-tertiary">
                    {t("Contact")}
                  </Link>
                </div>

                <div className="faq-visual" aria-hidden="true">
                  <div className="faq-visual-grid" />
                  <Image src={faqThumb} alt="" loading="lazy" className="faq-visual-image" />
                </div>
              </div>
            </div>

            <div className="col-lg-7 col-xl-8">
              <div className="faq-accordion" role="list">
                {faqData.map((item) => {
                  const isOpen = openId === item.id;
                  const buttonId = `faq-button-${item.id}`;
                  const panelId = `faq-panel-${item.id}`;

                  return (
                    <article
                      key={item.id}
                      className={`faq-card-revamp${isOpen ? " is-open" : ""}`}
                      role="listitem"
                    >
                      <button
                        id={buttonId}
                        type="button"
                        className="faq-card-toggle"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => toggleItem(item.id)}
                      >
                        <span className="faq-card-number">{String(item.id).padStart(2, "0")}</span>
                        <span className="faq-card-copy">
                          <span className="faq-card-title">{t(item.titleKey)}</span>
                        </span>
                        <span className="faq-card-indicator" aria-hidden="true">
                          <span className="faq-card-indicator-bar faq-card-indicator-bar-horizontal" />
                          <span className="faq-card-indicator-bar faq-card-indicator-bar-vertical" />
                        </span>
                      </button>

                      <div
                        id={panelId}
                        className="faq-card-panel"
                        role="region"
                        aria-labelledby={buttonId}
                        aria-hidden={!isOpen}
                      >
                        <div className="faq-card-panel-inner">
                          <p className="faq-text faq-answer">{t(item.descKey)}</p>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <GetInvolvedButton />
      </div>

      <style jsx>{`
        .faq-section-revamp {
          position: relative;
          --faq-shell-radius: 36px;
          --faq-inline-pad: 26px;
          --faq-leading-space: 66px;
        }

        .faq-shell {
          position: relative;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: var(--faq-shell-radius);
          padding: clamp(20px, 2.5vw, 32px);
          background:
            radial-gradient(circle at top left, rgba(120, 255, 214, 0.1), transparent 34%),
            linear-gradient(145deg, rgba(7, 41, 72, 0.96), rgba(2, 18, 33, 0.98));
          box-shadow: 0 28px 80px rgba(0, 0, 0, 0.24);
        }

        .faq-shell:before {
          content: "";
          position: absolute;
          inset: 1px;
          border-radius: calc(var(--faq-shell-radius) - 1px);
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0)),
            rgba(1, 12, 24, 0.22);
          pointer-events: none;
        }

        .faq-grid {
          position: relative;
          z-index: 1;
        }

        .faq-sidebar {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 24px;
          height: 100%;
          min-width: 0;
        }

        .faq-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding: 8px 14px;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(255, 255, 255, 0.06);
          color: #d8ebf7;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .faq-intro-copy {
          max-width: 42ch;
          margin-top: 16px;
        }

        .faq-pill-list {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .faq-pill {
          display: inline-flex;
          align-items: center;
          flex: 1 1 170px;
          min-width: 0;
          min-height: 46px;
          padding: 10px 14px;
          border-radius: 18px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.86);
          font-size: 14px;
          line-height: 1.45;
        }

        .faq-links {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .faq-link {
          min-height: 50px;
          padding-inline: 18px;
          justify-content: center;
          text-align: center;
          flex: 1 1 100%;
          min-width: 0;
        }

        .faq-link-secondary {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.16);
          color: var(--tg-white);
        }

        .faq-link-secondary:hover,
        .faq-link-tertiary:hover {
          border-color: rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.06);
          color: var(--tg-white);
        }

        .faq-link-tertiary {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: var(--tg-white);
        }

        .faq-visual {
          position: relative;
          margin-top: auto;
          min-height: clamp(220px, 28vw, 280px);
          border-radius: 28px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          overflow: hidden;
          background:
            radial-gradient(circle at center, rgba(120, 255, 214, 0.2), transparent 55%),
            linear-gradient(180deg, rgba(6, 38, 67, 0.2), rgba(6, 38, 67, 0.92));
        }

        .faq-visual-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
          background-size: 28px 28px;
          mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.6), transparent 90%);
        }

        .faq-visual-image {
          position: absolute;
          inset: auto 0 0 auto;
          width: 100%;
          max-height: 100%;
          height: auto;
          opacity: 0.78;
          object-fit: contain;
          object-position: center bottom;
          transform: translateY(18px) scale(1.04);
        }

        .faq-accordion {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: clamp(14px, 1.8vw, 18px);
          height: 100%;
          min-width: 0;
        }

        .faq-card-revamp {
          min-width: 0;
          border-radius: 28px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background:
            linear-gradient(180deg, rgba(6, 38, 67, 0.9), rgba(3, 20, 35, 0.96));
          box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
          overflow: hidden;
          transition:
            transform 0.25s ease,
            border-color 0.25s ease,
            background 0.25s ease,
            box-shadow 0.25s ease;
        }

        .faq-card-revamp:hover {
          transform: translateY(-2px);
          border-color: rgba(255, 255, 255, 0.22);
        }

        .faq-card-revamp.is-open {
          border-color: rgba(120, 255, 214, 0.3);
          background:
            linear-gradient(180deg, rgba(8, 49, 85, 0.96), rgba(3, 20, 35, 0.98));
          box-shadow: 0 22px 60px rgba(0, 0, 0, 0.24);
        }

        .faq-card-toggle {
          width: 100%;
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: start;
          gap: clamp(12px, 1.6vw, 18px);
          padding: var(--faq-inline-pad);
          border: 0;
          background: transparent;
          color: inherit;
          text-align: start;
        }

        .faq-card-toggle:focus-visible {
          outline: 2px solid rgba(120, 255, 214, 0.6);
          outline-offset: -2px;
        }

        .faq-card-number {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          width: 48px;
          height: 48px;
          border-radius: 16px;
          background: linear-gradient(
            135deg,
            rgba(120, 255, 214, 0.18),
            rgba(29, 182, 255, 0.12)
          );
          border: 1px solid rgba(255, 255, 255, 0.12);
          color: var(--tg-white);
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.08em;
          margin-top: 2px;
        }

        .faq-card-copy {
          min-width: 0;
        }

        .faq-card-title {
          display: block;
          color: var(--tg-white);
          font-family: var(--tg-heading-font-family);
          font-size: clamp(1.05rem, 0.95rem + 0.45vw, 1.3125rem);
          font-weight: 600;
          line-height: 1.45;
          overflow-wrap: anywhere;
        }

        .faq-card-indicator {
          position: relative;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(255, 255, 255, 0.04);
          flex-shrink: 0;
          align-self: center;
          justify-self: end;
        }

        .faq-card-indicator-bar {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 16px;
          height: 2px;
          border-radius: 999px;
          background: var(--tg-white);
          transform: translate(-50%, -50%);
          transition: transform 0.22s ease, opacity 0.22s ease;
        }

        .faq-card-indicator-bar-vertical {
          transform: translate(-50%, -50%) rotate(90deg);
        }

        .faq-card-revamp.is-open .faq-card-indicator {
          background: rgba(120, 255, 214, 0.12);
          border-color: rgba(120, 255, 214, 0.28);
        }

        .faq-card-revamp.is-open .faq-card-indicator-bar-vertical {
          transform: translate(-50%, -50%) rotate(90deg) scaleX(0);
          opacity: 0;
        }

        .faq-card-panel {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.28s ease;
        }

        .faq-card-revamp.is-open .faq-card-panel {
          grid-template-rows: 1fr;
        }

        .faq-card-panel-inner {
          overflow: hidden;
          min-width: 0;
        }

        .faq-answer {
          margin: 0;
          padding-inline: var(--faq-inline-pad);
          padding-inline-start: calc(
            var(--faq-inline-pad) + var(--faq-leading-space)
          );
          padding-block-end: var(--faq-inline-pad);
          color: rgba(255, 255, 255, 0.76);
          font-size: 16px;
          line-height: 1.8;
          overflow-wrap: anywhere;
        }

        @media (max-width: 1199px) {
          .faq-section-revamp {
            --faq-shell-radius: 30px;
            --faq-inline-pad: 22px;
            --faq-leading-space: 60px;
          }

          .faq-shell {
            padding: 28px;
          }

          .faq-intro-copy {
            max-width: none;
          }

          .faq-visual {
            min-height: 240px;
          }
        }

        @media (max-width: 991px) {
          .faq-sidebar {
            gap: 20px;
          }
        }

        @media (max-width: 767px) {
          .faq-section-revamp {
            --faq-shell-radius: 28px;
            --faq-inline-pad: 20px;
            --faq-leading-space: 0px;
          }

          .faq-shell {
            padding: 20px;
            border-radius: 28px;
          }

          .faq-card-toggle {
            grid-template-columns: auto minmax(0, 1fr);
            row-gap: 14px;
          }

          .faq-card-number {
            width: 42px;
            height: 42px;
            border-radius: 14px;
            font-size: 14px;
          }

          .faq-card-copy {
            grid-column: 1 / -1;
          }

          .faq-card-indicator {
            grid-column: 2;
            grid-row: 1;
            justify-self: end;
            width: 38px;
            height: 38px;
          }

          .faq-card-title {
            font-size: 18px;
          }

          .faq-answer {
            padding-inline-start: var(--faq-inline-pad);
          }

          .faq-visual {
            min-height: 220px;
          }
        }

        @media (max-width: 479px) {
          .faq-pill {
            flex-basis: 100%;
          }
        }
      `}</style>
    </section>
  );
};

export default FaqArea;
